import { PublicClientApplication, LogLevel, InteractionRequiredAuthError, EventType } from '@azure/msal-browser';

// True for NutraShore (MSAL). False when VITE_AUTH_TYPE=email (Rochem, AMPAK).
export const isMsalEnabled = import.meta.env.VITE_AUTH_TYPE !== 'email';

const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;
const tenantId = import.meta.env.VITE_AZURE_TENANT_ID;
const redirectUri = import.meta.env.VITE_AZURE_REDIRECT_URI ?? window.location.origin;

export const msalConfig = {
  auth: {
    clientId: clientId ?? 'dev-placeholder',
    authority: `https://login.microsoftonline.com/${tenantId ?? 'common'}`,
    redirectUri,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    // Must be false — true causes interaction_in_progress lock to persist
    // after sessionStorage is cleared (tab close), blocking next login.
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error('[MSAL]', message);
      },
      logLevel: LogLevel.Warning,
    },
  },
};

export const loginRequest = {
  scopes: [
    import.meta.env.VITE_AZURE_API_SCOPE ?? 'openid profile email',
  ],
};

// Lazy — only instantiated when isMsalEnabled is true, so Rollup can
// dead-code-eliminate the entire MSAL branch for email-auth tenants.
let _msalInstance = null;
export function getMsalInstance() {
  if (!_msalInstance) {
    _msalInstance = new PublicClientApplication(msalConfig);
    // AUTH-MSAL-REDIRECT-RACE-1 fix #3: loginRedirect/acquireTokenRedirect/logoutRedirect
    // below all pass `state` as the path the user was on before the interactive redirect —
    // redirectUri itself is always the bare origin (AAD has no way to preserve a deep link
    // on its own), so this is what actually lands the user back where they meant to go.
    _msalInstance.addEventCallback((event) => {
      const state = event?.payload?.state;
      if (!state) return;
      if (
        event.eventType !== EventType.LOGIN_SUCCESS &&
        event.eventType !== EventType.ACQUIRE_TOKEN_SUCCESS &&
        event.eventType !== EventType.LOGOUT_SUCCESS
      ) return;
      const here = window.location.pathname + window.location.search + window.location.hash;
      if (state !== here) window.location.replace(state);
    });
  }
  return _msalInstance;
}

/** Returns the active account or null. */
export function getAccount() {
  const accounts = getMsalInstance().getAllAccounts();
  return accounts.length > 0 ? accounts[0] : null;
}

// AUTH-MSAL-REDIRECT-RACE-1 fix #1: an in-memory guard, not persisted storage — it only needs
// to survive the same page load's burst of concurrent API calls (a page can fire several
// mount-time requests, each independently discovering the token needs interaction). Once the
// browser actually navigates away for the redirect, this module reloads fresh and the guard
// naturally resets; there is nothing to clean up on return.
let _interactiveRedirectPending = false;

function currentPathState() {
  return window.location.pathname + window.location.search + window.location.hash;
}

function beginInteractiveRedirect() {
  if (_interactiveRedirectPending) return false;
  _interactiveRedirectPending = true;
  return true;
}

/** True while an interactive MSAL redirect (login/token/logout) is already in flight — lets
 * callers outside this module (api.js's response interceptor) avoid racing it with their own
 * navigation. AUTH-MSAL-REDIRECT-RACE-1 fix #2. */
export function isInteractiveRedirectPending() {
  return _interactiveRedirectPending;
}

/** Starts an interactive sign-in redirect, preserving the current path as `state`. Serialized —
 * a second concurrent call while one is already in flight is a no-op instead of racing it.
 * Exported so SignInPage can use the same serialization/state-capture as the automatic paths
 * below, rather than calling loginRedirect() directly. */
export function loginWithRedirect() {
  if (!beginInteractiveRedirect()) return;
  getMsalInstance().loginRedirect({ ...loginRequest, state: currentPathState() })
    .catch(() => { _interactiveRedirectPending = false; });
}

/** AUTH-MSAL-REDIRECT-RACE-1 fix #4: a token MSAL still considers valid (an account is present,
 * silent acquisition wasn't the thing that failed) but that the backend keeps rejecting needs a
 * REAL sign-out — never just a client-side reload, which leaves the stale account in place and
 * repeats the same 401 forever. Serialized against the same in-flight guard as loginWithRedirect/
 * acquireTokenRedirect below, so this never races an interactive redirect already underway. */
export function forceMsalSignOut() {
  if (!beginInteractiveRedirect()) return;
  const account = getAccount();
  getMsalInstance().logoutRedirect({ account, state: currentPathState(), postLogoutRedirectUri: redirectUri })
    .catch(() => { _interactiveRedirectPending = false; });
}

/** Silently acquires a token; falls back to interactive redirect only for token errors. */
export async function getAccessToken() {
  const account = getAccount();
  if (!account) return null;
  try {
    const response = await getMsalInstance().acquireTokenSilent({ ...loginRequest, account });
    return response.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // AUTH-MSAL-REDIRECT-RACE-1 fix #1: serialized — concurrent callers from other in-flight
      // requests no-op here instead of each independently firing acquireTokenRedirect().
      if (beginInteractiveRedirect()) {
        getMsalInstance().acquireTokenRedirect({ ...loginRequest, account, state: currentPathState() })
          .catch(() => { _interactiveRedirectPending = false; });
      }
    }
    return null;
  }
}
